@if (loading$()) {
	<div class="mytrip-loading">
		<div class="mytrip-loading__spinner"></div>
		<p>{{ 'MYTRIP.LOADING' | translate }}</p>
	</div>
} @else if (error$()) {
	<div class="mytrip-error">
		<div class="mytrip-error__icon">✈</div>
		<h2>{{ 'MYTRIP.TRIP_NOT_FOUND' | translate }}</h2>
		<p>{{ 'MYTRIP.TRIP_NOT_FOUND_DESC' | translate }}</p>
	</div>
} @else if (trip$(); as t) {

	<!-- ─── Header ────────────────────────────────────────── -->
	<header class="mytrip-header">
		<div class="mytrip-header__inner">
			<div>
				<span class="mytrip-header__label">{{ 'MYTRIP.YOUR_TRIP' | translate }}</span>
				<h1 class="mytrip-header__title">{{ t.header?.name }}</h1>
			</div>
			@if (t.header?.destination) {
				<div class="mytrip-header__badge">
					<i class="fa fa-map-marker-alt"></i>
					{{ t.header!.destination }}
				</div>
			}
		</div>
	</header>

	<div class="mytrip-content">

		<!-- ─── Stats ────────────────────────────────────────── -->
		<div class="mytrip-stats">
			<div class="mytrip-stat">
				<div class="mytrip-stat__icon"><i class="fa fa-calendar-alt"></i></div>
				<div>
					<div class="mytrip-stat__value">{{ t.header?.departureDate | date: 'd MMM yyyy' : undefined : locale }}</div>
					<div class="mytrip-stat__label">{{ 'MYTRIP.DEPARTURE' | translate }}</div>
				</div>
			</div>
			<div class="mytrip-stat">
				<div class="mytrip-stat__icon"><i class="fa fa-calendar-check"></i></div>
				<div>
					<div class="mytrip-stat__value">{{ t.header?.returnDate | date: 'd MMM yyyy' : undefined : locale }}</div>
					<div class="mytrip-stat__label">{{ 'MYTRIP.RETURN' | translate }}</div>
				</div>
			</div>
			<div class="mytrip-stat">
				<div class="mytrip-stat__icon"><i class="fa fa-moon"></i></div>
				<div>
					<div class="mytrip-stat__value">{{ t.header?.nights }}</div>
					<div class="mytrip-stat__label">{{ 'MYTRIP.NIGHTS' | translate }}</div>
				</div>
			</div>
			<div class="mytrip-stat">
				<div class="mytrip-stat__icon"><i class="fa fa-users"></i></div>
				<div>
					<div class="mytrip-stat__value">{{ t.header?.paxesNum ?? 0 }}</div>
					<div class="mytrip-stat__label">{{ 'MYTRIP.PASSENGERS' | translate }}</div>
				</div>
			</div>
		</div>

		<!-- ─── Passengers ───────────────────────────────────── -->
		@if (t.paxes?.length) {
			<section class="mytrip-section">
				<h2 class="mytrip-section__title">
					<i class="fa fa-user"></i>
					{{ 'MYTRIP.PASSENGERS' | translate }}
				</h2>
				<div class="mytrip-card">
					@for (pax of t.paxes!; track $index; let i = $index) {
						<div class="mytrip-pax">
							<div class="mytrip-pax__number">{{ i + 1 }}</div>
							<div class="mytrip-pax__info">
								<span class="mytrip-pax__name">{{ pax.firstName }} {{ pax.lastName }}</span>
							</div>
						</div>
					}
				</div>
			</section>
		}

		<!-- ─── Flights ──────────────────────────────────────── -->
		@if (t.flightPlan?.length) {
			<section class="mytrip-section">
				<h2 class="mytrip-section__title">
					<i class="fa fa-plane"></i>
					{{ 'MYTRIP.FLIGHTS' | translate }}
				</h2>
				<div class="mytrip-flights">
					@for (seg of t.flightPlan!; track $index) {
						<div class="mytrip-flight">
							<div class="mytrip-flight__route">
								<div class="mytrip-flight__airport">
									<div class="mytrip-flight__code">{{ seg.departureAirportCode }}</div>
									<div class="mytrip-flight__city">{{ seg.departureAirportName || seg.departureAirportCode }}</div>
									<div class="mytrip-flight__time">{{ seg.departureDate | date: 'HH:mm' : undefined : locale }}</div>
								</div>
								<div class="mytrip-flight__arrow">
									<div class="mytrip-flight__line"></div>
									<i class="fa fa-plane"></i>
									<div class="mytrip-flight__line"></div>
								</div>
								<div class="mytrip-flight__airport">
									<div class="mytrip-flight__code">{{ seg.arrivalAirportCode }}</div>
									<div class="mytrip-flight__city">{{ seg.arrivalAirportName || seg.arrivalAirportCode }}</div>
									<div class="mytrip-flight__time">
										{{ seg.arrivalDate | date: 'HH:mm' : undefined : locale }}
										@if (seg.arrivalNextDay) {
											<span class="mytrip-flight__nextday">+1</span>
										}
									</div>
								</div>
							</div>
							<div class="mytrip-flight__details">
								@if (seg.departureDate) {
									<span><i class="fa fa-calendar-alt"></i> {{ seg.departureDate | date: 'EEE, d MMM yyyy' : undefined : locale }}</span>
								}
								@if (seg.airlineName) {
									<span><i class="fa fa-tag"></i> {{ seg.airlineName }}</span>
								}
								@if (seg.airlineCode && seg.flightNumber) {
									<span><i class="fa fa-hashtag"></i> {{ seg.airlineCode }}{{ seg.flightNumber }}</span>
								}
								@if (seg.pnr) {
									<span><i class="fa fa-barcode"></i> {{ 'MYTRIP.PNR_LABEL' | translate }}: {{ seg.pnr }}</span>
								}
								@if (seg.class) {
									<span><i class="fa fa-star"></i> {{ seg.class }}</span>
								}
							</div>
						</div>
					}
				</div>
			</section>
		}

		<!-- ─── Itinerary ────────────────────────────────────── -->
		@if (t.itinerary?.length) {
			<section class="mytrip-section">
				<h2 class="mytrip-section__title">
					<i class="fa fa-route"></i>
					{{ 'MYTRIP.ITINERARY' | translate }}
				</h2>
				<div class="mytrip-itinerary">
					@for (step of t.itinerary!; track $index; let last = $last) {
						<div class="mytrip-step">
							<div class="mytrip-step__timeline">
								<div class="mytrip-step__dot"></div>
								@if (!last) {
									<div class="mytrip-step__line"></div>
								}
							</div>
							<div class="mytrip-step__content">
								<div class="mytrip-step__header">
									<h3 class="mytrip-step__location">{{ step.destination }}</h3>
									@if (step.arrivalDate) {
										<div class="mytrip-step__dates">
											{{ step.arrivalDate | date: 'd MMM' : undefined : locale }}
											@if (step.departureDate) {
												– {{ step.departureDate | date: 'd MMM yyyy' : undefined : locale }}
											}
										</div>
									}
								</div>
								@if (step.coverImage) {
									<div class="mytrip-cover">
										<img [src]="step.coverImage" [alt]="step.destination || ''" loading="lazy">
										@if (step.destination) {
											<div class="mytrip-cover__caption">{{ step.destination }}</div>
										}
									</div>
								}
								@if (step.services?.length) {
									<div class="mytrip-services">
										@for (svc of step.services!; track $index) {
											<div class="mytrip-svc" [class.mytrip-svc--has-img]="svc.imageUrl">
												@if (svc.imageUrl) {
													<div class="mytrip-svc__thumb">
														<img [src]="svc.imageUrl" [alt]="svc.name || ''" loading="lazy">
													</div>
												} @else {
													<div class="mytrip-svc__icon">
														<i class="fa fa-concierge-bell"></i>
													</div>
												}
												<div class="mytrip-svc__body">
													@if (svc.name) {
														<div class="mytrip-svc__name">{{ svc.name }}</div>
													}
													@if (svc.description) {
														<div class="mytrip-svc__desc" [innerHTML]="svc.description"></div>
													}
												</div>
											</div>
										}
									</div>
								}
							</div>
						</div>
					}
				</div>
			</section>
		}

		<!-- ─── Travel Documents ─────────────────────────────── -->
		@if (isDocumentsVisible && t.documents?.length) {
			<section class="mytrip-section">
				<h2 class="mytrip-section__title">
					<i class="fa fa-file-alt"></i>
					{{ 'MYTRIP.TRAVEL_DOCUMENTS' | translate }}
				</h2>
				<div class="mytrip-docs">
					@for (doc of t.documents!; track doc.id) {
						<a
							class="mytrip-doc"
							[href]="doc.publicUrl"
							target="_blank"
							rel="noopener noreferrer">
							<div class="mytrip-doc__icon"><i class="fa fa-file-pdf"></i></div>
							<div class="mytrip-doc__body">
								<div class="mytrip-doc__name">{{ doc.name }}</div>
							</div>
							<i class="fa fa-download mytrip-doc__action"></i>
						</a>
					}
				</div>
			</section>
		}

		<!-- ─── Insurance ────────────────────────────────────── -->
		@if (isDocumentsVisible && t.insurances?.length) {
			<section class="mytrip-section">
				<h2 class="mytrip-section__title">
					<i class="fa fa-shield-alt"></i>
					{{ 'MYTRIP.INSURANCE' | translate }}
				</h2>
				<div class="mytrip-services">
					@for (ins of t.insurances!; track $index) {
						<div class="mytrip-svc">
							<div class="mytrip-svc__icon"><i class="fa fa-shield-alt"></i></div>
							<div class="mytrip-svc__body">
								<div class="mytrip-svc__name">{{ ins.name }}</div>
								@if (ins.description) {
									<div class="mytrip-svc__desc">{{ ins.description }}</div>
								}
							</div>
						</div>
					}
				</div>
			</section>
		}

	</div>
}
